import React, { useState, useMemo } from "react";
import { FiSearch, FiEdit2, FiTrash2, FiInbox, FiPercent, FiPackage, FiLink, FiCalendar, FiPlus } from "react-icons/fi";

function SalesAndDealsTable({ data, onEdit, onDelete, onToggleStatus, onAddNew }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const getDealStatus = (deal) => {
    const now = new Date();
    const start = new Date(deal.start_date);
    const end = new Date(deal.end_date);
    if (!deal.is_active) return "inactive";
    if (start > now) return "scheduled";
    if (end < now) return "expired";
    return "active";
  };

  const statusPillClass = (status) => {
    switch (status) {
      case "active":
        return "sd-status-active";
      case "inactive":
        return "sd-status-inactive";
      case "scheduled":
        return "sd-status-scheduled";
      case "expired":
        return "sd-status-expired";
      default:
        return "sd-status-inactive";
    }
  };

  const statusLabel = (status) => status.charAt(0).toUpperCase() + status.slice(1);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((deal) => {
      const matchesSearch =
        !searchQuery ||
        deal.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.slug?.toLowerCase().includes(searchQuery.toLowerCase());
      const dealStatus = getDealStatus(deal);
      const matchesStatus = statusFilter === "all" || dealStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, searchQuery, statusFilter]);

  const itemCount = (deal) => {
    try {
      const listings = typeof deal.listings === "string" ? JSON.parse(deal.listings) : deal.listings;
      return Array.isArray(listings) ? listings.length : 0;
    } catch {
      return 0;
    }
  };

  return (
    <>
      <div className="sd-toolbar">
        <div className="sd-search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by deal name or slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="sd-status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="scheduled">Scheduled</option>
          <option value="expired">Expired</option>
        </select>
        <span className="sd-result-count">
          {filteredData.length} deal{filteredData.length !== 1 ? "s" : ""}
        </span>
      </div>

      {filteredData.length > 0 ? (
        <div className="sd-table-wrapper">
          <div className="table-responsive">
            <table className="table sd-table align-middle">
              <thead>
                <tr>
                  <th>Deal Name</th>
                  <th>Duration</th>
                  <th>Discount</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((deal) => {
                  const status = getDealStatus(deal);
                  const count = itemCount(deal);
                  return (
                    <tr key={deal.sale_id}>
                      <td>
                        <div className="sd-deal-name">{deal.name}</div>
                        {deal.slug && (
                          <a
                            className="sd-deal-slug"
                            href={`${process.env.REACT_APP_MARKETPLACE_URL}/sale/${deal.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FiLink style={{ fontSize: "0.7rem" }} />
                            /sale/{deal.slug}
                          </a>
                        )}
                      </td>
                      <td>
                        <div className="sd-duration">
                          <FiCalendar style={{ fontSize: "0.75rem" }} />
                          {formatDate(deal.start_date)} &rarr; {formatDate(deal.end_date)}
                        </div>
                      </td>
                      <td>
                        <span className="sd-chip sd-chip-discount">
                          <FiPercent style={{ fontSize: "0.7rem" }} />
                          {deal.discount_upto}% off
                        </span>
                      </td>
                      <td>
                        <span className="sd-chip sd-chip-items">
                          <FiPackage style={{ fontSize: "0.7rem" }} />
                          {count}
                        </span>
                      </td>
                      <td>
                        <div className="sd-status-cell">
                          <span className={`sd-status-pill ${statusPillClass(status)}`}>{statusLabel(status)}</span>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input sd-switch"
                              type="checkbox"
                              checked={deal.is_active === 1}
                              onChange={() => onToggleStatus(deal.sale_id)}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="sd-actions">
                          <button className="sd-action-btn" onClick={() => onEdit(deal)} title="Edit">
                            <FiEdit2 size={14} />
                          </button>
                          <button className="sd-action-btn danger" onClick={() => onDelete(deal.sale_id)} title="Delete">
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">
            <FiInbox size={28} />
          </div>
          <div className="sd-empty-title">No deals found</div>
          <div className="sd-empty-subtitle">
            {data && data.length > 0 ? "Try adjusting your search or filter." : "Get started by creating your first deal."}
          </div>
          {(!data || data.length === 0) && onAddNew && (
            <button className="btn btn-primary sd-btn-toggle mt-3" onClick={onAddNew}>
              <FiPlus /> Add New Deal
            </button>
          )}
        </div>
      )}
    </>
  );
}

export default SalesAndDealsTable;
